import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { queueMeshWarm } from "../spineMeshWarm";

// Configuration constants (export for easy tweaking)
export const WIN_LINES_CONFIG = {
  widthMultiplier: 0.3,
  heightMultiplier: 0.326,
  xOffsetMultiplier: -0.18,
  yOffsetMultiplier: -0.9,
  // true: the spine is authored in board units on the grid centre — no fit
  // (the multipliers above are the old fit-to-frame placement).
  NATIVE: true,
  // Half the width the lines may show (native units) — the board's inner
  // edge, where the pillars start (5 × 214 / 2). 0 = no cut.
  CLIP_HALF_WIDTH: (Settings.REEL_COUNT * Settings.SYMBOL_WIDTH) / 2,
};

// REJECTED (2026-10-14: "we never shown all lines") — kept for reference:
// /**
//  * ── Only lines that add a NEW path (global win, 2026-10-14) ── on 4 rows the
//  * 20 lines share paths, and the pieces are additive: shared paths stacked to
//  * 2-3x the glow. A line is shown only if none of its visible pieces (a half
//  * cell, centre -> edge, outside the win boxes) is drawn by a line already
//  * picked; otherwise the whole line is left out. Vladimir: "if they repeat we
//  * dont show all of them, only one", "not parts just dont show that line".
//  *
//  * @param {Array<{lineId, rows: number[], hiddenReels?: number[]}>} lines in
//  *        show order; `rows` the line's row per reel, `hiddenReels` the reels
//  *        where it crosses a win box (boxedReelsOnLine).
//  * @returns {Array} the lineIds to show.
//  */
// export const uniqueWinLineIds = (lines) => {
//   const drawn = new Set();
//   const picked = [];
//   (lines || []).forEach(({ lineId, rows, hiddenReels }) => {
//     if (lineId == null) return;
//     if (!Array.isArray(rows)) {
//       picked.push(lineId);
//       return;
//     }
//     const hidden = new Set(hiddenReels || []);
//     const last = rows.length - 1;
//     const pieces = [];
//     rows.forEach((row, r) => {
//       if (hidden.has(r)) return;
//       pieces.push(`L${r}:${row}:${r > 0 ? rows[r - 1] : row}`);
//       pieces.push(`R${r}:${row}:${r < last ? rows[r + 1] : row}`);
//     });
//     if (pieces.some((key) => drawn.has(key))) return;
//     pieces.forEach((key) => drawn.add(key));
//     picked.push(lineId);
//   });
//   return picked;
// };

// ── The corners' own layer (2026-10-14) ── every line's corner pieces are
// kept (dropping some left notches on bends that shared a cell: mirror bends,
// or a 45-degree beside a 90-degree on the same side). They are moved into
// ONE layer over the lines, where they combine "brightest wins" (WebGL MAX)
// — overlapping corners never add up — and that layer is added onto the
// lines as a whole (a filter compositing with ADD), so against its own line a
// corner is additive exactly as before. (MAX straight onto the lines left a
// dim seam on every corner: the art is made to ADD onto its line's soft cut
// ends — reverted.) A custom PIXI blend id (31 — free, and inside the 0..31
// premultiplied table the batcher maps through), registered once on the
// renderer. No MAX support (a WebGL1 without EXT_blend_minmax): the layer
// stays additive inside and corners fall back to one per cell per side.
const CORNER_MAX_BLEND = 31;
let cornerMaxBlendReady = null;
const ensureCornerMaxBlend = () => {
  if (cornerMaxBlendReady !== null) return cornerMaxBlendReady;
  const renderer = globalThis.__PIXI_RENDERER__;
  const gl = renderer?.gl;
  if (!gl || !renderer.state?.blendModes) return false; // not yet — ask again later
  const isGL2 =
    typeof WebGL2RenderingContext !== "undefined" && gl instanceof WebGL2RenderingContext;
  const maxEq = isGL2 ? gl.MAX : gl.getExtension("EXT_blend_minmax")?.MAX_EXT;
  cornerMaxBlendReady = !!maxEq;
  if (maxEq) {
    renderer.state.blendModes[CORNER_MAX_BLEND] = [gl.ONE, gl.ONE, gl.ONE, gl.ONE, maxEq, maxEq];
  }
  return cornerMaxBlendReady;
};

// Threshold: when active lines exceed this, skip-frame updates kick in
const SKIP_FRAME_THRESHOLD = 2;

/**
 * The reels where a line runs through a boxed cell — no line is drawn there
 * (2026-10-13). `lineRows` is the line's row per reel; `positions` the boxed
 * cells, [reel, row].
 */
export const boxedReelsOnLine = (lineRows, positions) => {
  if (!Array.isArray(lineRows) || !Array.isArray(positions)) return [];
  const boxed = new Set(positions.map(([r, row]) => `${r}_${row}`));
  const reels = [];
  lineRows.forEach((row, r) => {
    if (boxed.has(`${r}_${row}`)) reels.push(r);
  });
  return reels;
};

/**
 * WinLinesController - Manages win line spine animations
 *
 * Architecture: Pre-warmed spine-per-line with skip-frame updates
 * - One spine instance per win line, all created and warmed at initialization
 * - Line count dynamically extracted from spine data (works for any game)
 * - autoUpdate disabled on all spines — manual update via shared ticker
 * - When 3+ lines active: updates every 2nd frame to halve bone processing cost
 * - Active-only tracking: show/hide/update only touches relevant spines
 */
class WinLinesController {
  constructor(container, symbolWidth, symbolHeight, dividerDimensions) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dividerDimensions = dividerDimensions;

    // One spine instance per win line, keyed by line ID
    this.winLineSpines = {};
    this.spineContainer = new PIXI.Container();
    this.currentLineId = null;
    this.initialized = false;

    // Dynamically discovered from spine data
    this.winLineCount = 0;
    this.availableAnimations = [];

    // Track currently active line IDs (avoids iterating all spines)
    this.activeLineIds = [];

    // ── No line inside a win box (2026-10-13) ── lineId -> the slots (line
    // halves) to keep blank. A half lies inside ONE cell (centre -> edge), so
    // a boxed cell on reel r hides halves h(2r+1) and h(2r+2). Blanked after
    // every update (the clip sets them back each frame).
    this.hiddenByLine = {};

    // Lines due to start on the next frame, WITH their win boxes (WinBoxController
    // defers its boxes one frame the same way).
    this._deferredRAF = null;

    // Skip-frame update state
    this._frameCounter = 0;
    this._tickerBound = this._onTick.bind(this);
    this._tickerActive = false;

    // Internal caches for performance optimization
    this._scaleAndPositionCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    // Add spine container to main container
    this.container.addChild(this.spineContainer);
    // The corners' layer (see CORNER_MAX_BLEND), over every line.
    this.spineContainer.sortableChildren = true;
    this.cornerLayer = new PIXI.Container();
    this.cornerLayer.name = "win_lines_corners";
    this.cornerLayer.zIndex = 1;
    const cornerFilter = new PIXI.AlphaFilter(1);
    cornerFilter.blendMode = PIXI.BLEND_MODES.ADD;
    cornerFilter.resolution = null; // the screen's own — sharp on every DPR
    this.cornerLayer.filters = [cornerFilter];
    this.spineContainer.addChild(this.cornerLayer);

    // ── Cut at the pillars (2026-10-13) ── the lines run oversized past the
    // board; this rectangle (board width, generous height) hides both ends
    // where the pillars start. Axis-aligned, so Pixi uses a scissor — cheap.
    if (WIN_LINES_CONFIG.NATIVE && WIN_LINES_CONFIG.CLIP_HALF_WIDTH > 0) {
      const halfW = WIN_LINES_CONFIG.CLIP_HALF_WIDTH;
      const halfH = Settings.SYMBOL_HEIGHT * Settings.SYMBOLS_VISIBLE;
      this.clipMask = new PIXI.Graphics();
      this.clipMask.name = "win_lines_clip";
      this.clipMask.beginFill(0xffffff, 1);
      this.clipMask.drawRect(-halfW, -halfH, halfW * 2, halfH * 2);
      this.clipMask.endFill();
      this.container.addChild(this.clipMask);
      this.spineContainer.mask = this.clipMask;
    }

    // Initialize all spines synchronously (same pattern as OverlayReelController)
    this.initializeSpines();
  }

  /**
   * Initialize spine instances — one per win line, all pre-warmed
   * Dynamically discovers win_line_* animation count from spine data
   */
  initializeSpines() {
    if (!this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Create a probe spine to discover available win_line_* animations
    const probeSpine = this.createWinLineSpine();
    if (!probeSpine || !probeSpine.skeleton || !probeSpine.state) {
      return;
    }

    // Extract all win_line_* animation names dynamically
    const allAnimations = this.getAnimationNames(probeSpine);
    this.availableAnimations = allAnimations.filter((name) =>
      name.startsWith("win_line_")
    );
    this.winLineCount = this.availableAnimations.length;

    if (this.winLineCount === 0) {
      probeSpine.destroy({ children: true, texture: false, baseTexture: false });
      return;
    }

    // Use probe spine as the first line's instance
    this.warmAndStoreSpine(probeSpine, this.availableAnimations[0], 0);

    // Create and warm remaining spines — one per win line
    for (let i = 1; i < this.winLineCount; i++) {
      const spine = this.createWinLineSpine();
      if (!spine || !spine.state) continue;

      this.warmAndStoreSpine(spine, this.availableAnimations[i], i);
    }

    // Scale and position the entire spine container
    this.scaleAndPositionSpineContainer();

    // Start with container hidden (GPU still processes at 0.001)
    this.spineContainer.alpha = 0.001;

    // ── Warmed like every other spine (2026-10-13) ── the atlas page on the
    // GPU now, and every line's slot meshes built ahead (a few spines per
    // frame) — not on the first win of the session.
    SpineAtlasUploader.upload("win_lines");
    Object.values(this.winLineSpines).forEach((spine) =>
      queueMeshWarm({ spine, isBusy: () => spine.state?.timeScale > 0 }),
    );

    this.initialized = true;
  }

  /**
   * Warm up a spine instance and store it in the pool
   * Sets animation, triggers GPU compilation, then pauses at frame 0
   * @param {Spine} spine - The spine instance to warm
   * @param {string} animationName - The win_line_* animation name
   * @param {number} lineIndex - Index into availableAnimations
   */
  warmAndStoreSpine(spine, animationName, lineIndex) {
    // autoUpdate OFF — we control updates manually via ticker
    spine.autoUpdate = false;
    this.spineContainer.addChild(spine);

    // GPU invisible but still in render pipeline
    spine.alpha = 0.001;

    // Set animation → triggers shader compilation and mesh creation
    spine.state.setAnimation(0, animationName, true);
    spine.state.update(0.016);

    // Populate bone/slot hierarchy for GPU
    try {
      spine.skeleton.updateWorldTransform();
    } catch (e) {
      // Physics not initialized on some spines — safe to continue
    }

    // Pause at frame 0 — GPU has already compiled everything
    spine.state.timeScale = 0;

    // Extract the numeric line ID from animation name (e.g., "win_line_3" → 3)
    const lineId = parseInt(animationName.replace("win_line_", ""), 10);
    this.winLineSpines[lineId] = spine;
  }

  /**
   * Get animation names from a spine instance
   */
  getAnimationNames(spine) {
    if (!spine?.skeleton?.data?.animations) return [];
    return spine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Create a single win lines spine instance
   */
  createWinLineSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_lines-skeleton";
      const atlasAlias = "win_lines-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      // Its corner meshes live in the corners' layer (see CORNER_MAX_BLEND).
      // spine-pixi builds and refreshes them in updateTransform (renderMeshes:
      // geometry, alpha, blend from the slot data) and only adds a mesh to
      // the spine when it creates it, so each is moved once, then kept on the
      // layer's blend. The layer shares the spine's space (both untransformed
      // in spineContainer). The spine's own alpha (0.001 while parked) does
      // not reach them there, so they follow it by hand.
      const cornerSlots = spine.skeleton.slots.filter((slot) => slot.data.name[0] === "j");
      const baseUpdateTransform = spine.updateTransform.bind(spine);
      spine.updateTransform = () => {
        baseUpdateTransform();
        const layer = this.cornerLayer;
        if (!layer || layer.destroyed) return;
        const blend = ensureCornerMaxBlend() ? CORNER_MAX_BLEND : PIXI.BLEND_MODES.ADD;
        const shown = spine.worldAlpha > 0.01 && spine.worldVisible;
        for (let i = 0; i < cornerSlots.length; i++) {
          const mesh = spine.meshesCache?.get(cornerSlots[i]);
          if (!mesh || mesh.destroyed) continue;
          if (mesh.parent !== layer) layer.addChild(mesh);
          mesh.blendMode = blend;
          mesh.renderable = shown;
        }
      };

      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Shared ticker callback — updates only active spines manually
   * When 1-2 lines: updates every frame (smooth)
   * When 3+ lines: updates every 2nd frame (halves bone cost)
   */
  _onTick() {
    const activeCount = this.activeLineIds.length;
    if (activeCount === 0) return;

    this._frameCounter++;

    // Skip every other frame when many lines are active
    if (activeCount > SKIP_FRAME_THRESHOLD && this._frameCounter % 2 !== 0) {
      return;
    }

    // Compute delta time (in seconds) — use PIXI ticker's deltaMS
    const dt = PIXI.Ticker.shared.deltaMS / 1000;
    // When skipping frames, compensate so animation speed stays correct
    const compensatedDt = activeCount > SKIP_FRAME_THRESHOLD ? dt * 2 : dt;

    for (let i = 0; i < activeCount; i++) {
      const lineId = this.activeLineIds[i];
      const spine = this.winLineSpines[lineId];
      if (spine && spine.state && spine.state.timeScale > 0) {
        spine.update(compensatedDt);
        // After the clip has set its pieces: blank the ones inside boxes.
        this._applyHidden(lineId);
      }
    }
    this._hideSharedPieces();
  }

  // ── First try (2026-10-14): keyed by attachment + bone, it missed the
  // shorter corner copies — replaced by the grid keys below. ──
  // /**
  //  * ── One copy of a shared path (2026-10-14) ── on 4 rows the 20 lines share
  //  * paths: a global win drew up to 3 identical pieces on top of each other,
  //  * and the pieces are additive, so a shared path burned 2-3x brighter. Every
  //  * piece (line half or joint) is keyed by its slot, attachment and bone
  //  * world transform; the first active line that draws it keeps it, the
  //  * others blank their copy. Run after every update — the clip sets its
  //  * pieces back each frame, so a line leaving hands its pieces on.
  //  */
  // _hideSharedPieces() {
  //   const ids = this.activeLineIds;
  //   if (ids.length < 2) return;
  //   const seen = new Set();
  //   const r = (v) => Math.round(v * 10);
  //   for (let i = 0; i < ids.length; i++) {
  //     const sk = this.winLineSpines[ids[i]]?.skeleton;
  //     if (!sk) continue;
  //     const slots = sk.slots;
  //     for (let j = 0; j < slots.length; j++) {
  //       const slot = slots[j];
  //       const att = slot.getAttachment();
  //       if (!att) continue;
  //       const b = slot.bone;
  //       const key = `${slot.data.index}|${att.name}|${r(b.worldX)}|${r(b.worldY)}|${r(b.a)}|${r(b.b)}|${r(b.c)}|${r(b.d)}`;
  //       if (seen.has(key)) slot.setAttachment(null);
  //       else seen.add(key);
  //     }
  //   }
  // }

  /**
   * ── One copy of a shared stretch (2026-10-14) ── on 4 rows the 20 lines
   * share stretches, and the pieces are additive: a shared stretch burned 2-3x
   * brighter in a global win. Every line is shown; each stretch is drawn by
   * ONE of them. A stretch is a slot's place on the grid, from the line's
   * rows — never the attachment or bone (a line turning at a cell draws the
   * same half shorter, ending at its corner piece):
   *   h0 / h11        the stubs past the board, at the first / last row
   *   h(2r+1)         reel r's left half: its row, the row it comes from
   *   h(2r+2)         reel r's right half: its row, the row it goes to
   *   j(r)            the corner at reel r: its cell and the side it turns to
   * The longest copy (bone scaleX) is kept, so no line shows a gap. Run after
   * every update — the clip sets its pieces back each frame, so a line
   * leaving hands its stretches on.
   */
  _hideSharedPieces() {
    const ids = this.activeLineIds;
    if (ids.length < 2 || !this.rowsByLine) return;
    const owners = new Map(); // stretch -> { slot, length }
    for (let i = 0; i < ids.length; i++) {
      const rows = this.rowsByLine[ids[i]];
      const sk = this.winLineSpines[ids[i]]?.skeleton;
      if (!sk || !Array.isArray(rows)) continue;
      const slots = sk.slots;
      for (let j = 0; j < slots.length; j++) {
        const slot = slots[j];
        if (!slot.getAttachment()) continue;
        const key = this._stretchOf(slot.data.name, rows);
        if (!key) continue;
        // const length = Math.abs(slot.bone.scaleX);
        // The longest copy; for a corner the bigger 90-degree one (wc).
        const length =
          slot.data.name[0] === "j"
            ? slot.getAttachment().name === "wc"
              ? 2
              : 1
            : Math.abs(slot.bone.scaleX);
        const owner = owners.get(key);
        if (!owner) {
          owners.set(key, { slot, length });
        } else if (length > owner.length) {
          owner.slot.setAttachment(null);
          owner.slot = slot;
          owner.length = length;
        } else {
          slot.setAttachment(null);
        }
      }
    }
  }

  /** A line slot's stretch on the grid (see _hideSharedPieces), or null. */
  _stretchOf(slotName, rows) {
    const last = rows.length - 1;
    const at = (r) => rows[Math.max(0, Math.min(last, r))];
    const n = parseInt(slotName.slice(1), 10);
    if (Number.isNaN(n)) return null;
    // if (slotName[0] === "j") return `j${n}:${at(n - 1)}:${at(n)}:${at(n + 1)}`;
    // ── One corner per CELL (2026-10-14) ── lines turning at the same cell
    // centre stacked up to 4 corners there (keyed by the bend, they never
    // matched). The lines crossing that cell cover the other bends; a corner
    // alone in its cell is untouched.
    // if (slotName[0] === "j") return `j${n}:${at(n)}`;
    // ── One corner per cell PER SIDE (2026-10-14) ── one per cell left a
    // gap: two lines bending into the same cell from above and from below
    // have their outer wedges on OPPOSITE sides, and the dropped one showed
    // as a dark notch. The side is the way the line turns there (rows grow
    // downward); same-side corners still draw once.
    // if (slotName[0] === "j") {
    //   const turn = at(n + 1) - at(n) - (at(n) - at(n - 1));
    //   return `j${n}:${at(n)}:${Math.sign(turn)}`;
    // }
    // ── Every bend keeps its corner (2026-10-14) ── one per side still
    // dropped a 45-degree corner beside a 90-degree one on the same side (a
    // notch). Corners draw with MAX now (CORNER_MAX_BLEND), so overlapping
    // ones do not stack: only IDENTICAL bends are drawn once. Without MAX,
    // the one-per-side rule stays.
    if (slotName[0] === "j") {
      if (ensureCornerMaxBlend()) return `j${n}:${at(n - 1)}:${at(n)}:${at(n + 1)}`;
      const turn = at(n + 1) - at(n) - (at(n) - at(n - 1));
      return `j${n}:${at(n)}:${Math.sign(turn)}`;
    }
    if (slotName[0] !== "h") return null;
    if (n === 0) return `s:${rows[0]}`;
    if (n === 2 * rows.length + 1) return `e:${rows[last]}`;
    const r = Math.floor((n - 1) / 2);
    return n % 2 === 1
      ? `l${r}:${at(r)}:${at(r - 1)}`
      : `r${r}:${at(r)}:${at(r + 1)}`;
  }

  /**
   * Start the manual update ticker
   */
  _startTicker() {
    if (!this._tickerActive) {
      this._frameCounter = 0;
      PIXI.Ticker.shared.add(this._tickerBound);
      this._tickerActive = true;
    }
  }

  /**
   * Stop the manual update ticker
   */
  _stopTicker() {
    if (this._tickerActive) {
      PIXI.Ticker.shared.remove(this._tickerBound);
      this._tickerActive = false;
      this._frameCounter = 0;
    }
  }

  /**
   * Scale and position the spine container
   * Cached for performance — invalidated when dimensions change
   */
  scaleAndPositionSpineContainer() {
    if (!this.spineContainer || !this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // ── Native board units (Cleopatra's Fire, 2026-10-13) ── the win_lines
    // spine is authored on the board itself (214-unit cells, origin = grid
    // centre) and the overlay is in those units: scale 1 at the origin.
    if (WIN_LINES_CONFIG.NATIVE) {
      this.spineContainer.scale.set(1, 1);
      this.spineContainer.position.set(0, 0);
      return;
    }

    // Check cache first
    if (
      this._scaleAndPositionCache &&
      this._lastSymbolWidth === this.symbolWidth &&
      this._lastSymbolHeight === this.symbolHeight
    ) {
      const cached = this._scaleAndPositionCache;
      this.spineContainer.scale.set(cached.scale, cached.scale);
      this.spineContainer.position.set(cached.x, cached.y);
      return;
    }

    const nativeSpineWidth = Settings.SYMBOL_WIDTH;
    const nativeSpineHeight = Settings.SYMBOL_HEIGHT;

    const cfg = WIN_LINES_CONFIG;
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * cfg.widthMultiplier;
    const targetFrameHeight =
      this.symbolHeight * Settings.SYMBOLS_VISIBLE * cfg.heightMultiplier;

    const scaleX = targetFrameWidth / nativeSpineWidth;
    const scaleY = targetFrameHeight / nativeSpineHeight;
    const scale = Math.min(scaleX, scaleY);

    const scaledWidth = nativeSpineWidth * scale;
    const scaledHeight = nativeSpineHeight * scale;

    const centerX = (targetFrameWidth - scaledWidth) / 2;
    const centerY = (targetFrameHeight - scaledHeight) / 2;

    const xOffset = targetFrameWidth * cfg.xOffsetMultiplier;
    const yOffset = targetFrameHeight * cfg.yOffsetMultiplier;

    const x = centerX + xOffset;
    const y = centerY + yOffset;

    this._scaleAndPositionCache = { scale, x, y };
    this._lastSymbolWidth = this.symbolWidth;
    this._lastSymbolHeight = this.symbolHeight;

    this.spineContainer.scale.set(scale, scale);
    this.spineContainer.position.set(x, y);
  }

  /**
   * Show multiple win lines simultaneously
   * Only touches the requested spines — all others remain paused and invisible
   * @param {Array<number|string>} lineIds - Array of line IDs to show
   */
  // showMultipleWinLines(lineIds) {
  // hiddenReelsByLine: { [lineId]: reel indices whose cell is boxed }.
  // showMultipleWinLines(lineIds, hiddenReelsByLine = null) {
  showMultipleWinLines(lineIds, hiddenReelsByLine = null, rowsByLine = null) {
    if (!this.initialized) {
      return;
    }
    // Each line's row per reel — _hideSharedPieces matches stretches by them.
    this.rowsByLine = rowsByLine || {};

    if (!Array.isArray(lineIds) || lineIds.length === 0) {
      return;
    }

    for (let i = 0; i < lineIds.length; i++) {
      this._setHidden(lineIds[i], hiddenReelsByLine?.[lineIds[i]]);
    }

    // ── Keep what is already burning (2026-10-13) ── as the win boxes do: a
    // line already up keeps playing, only new lines start, lines leaving are
    // parked. Restarting them all blinked every line and reset its fire.
    const wanted = lineIds.filter((id) => this.winLineSpines[id]?.state);
    if (wanted.length === 0) return;
    for (let i = 0; i < this.activeLineIds.length; i++) {
      const prevId = this.activeLineIds[i];
      if (!wanted.includes(prevId)) this._parkSpine(this.winLineSpines[prevId]);
    }
    // for (let i = 0; i < wanted.length; i++) {
    //   if (!this.activeLineIds.includes(wanted[i])) this._startSpine(wanted[i]);
    // }
    // this.activeLineIds = wanted;
    // this.currentLineId = "multiple";
    // this._startTicker();
    // this.spineContainer.alpha = 1;
    const kept = wanted.filter((id) => this.activeLineIds.includes(id));
    const fresh = wanted.filter((id) => !kept.includes(id));
    this.activeLineIds = kept;
    this.currentLineId = "multiple";
    this._startDeferred(fresh);

    // Before (restarted every line, every call):
    // // Hide container during setup
    // this.spineContainer.alpha = 0.001;
    //
    // // Stop ticker while setting up
    // this._stopTicker();
    //
    // // Pause only previously active spines (not all N)
    // for (let i = 0; i < this.activeLineIds.length; i++) {
    //   const prevLineId = this.activeLineIds[i];
    //   const spine = this.winLineSpines[prevLineId];
    //   if (spine && spine.state) {
    //     spine.alpha = 0.001;
    //     spine.state.timeScale = 0;
    //   }
    // }
    //
    // // Activate the requested line spines
    // const validLineIds = [];
    // for (let i = 0; i < lineIds.length; i++) {
    //   const lineId = lineIds[i];
    //   const spine = this.winLineSpines[lineId];
    //   if (!spine || !spine.state) continue;
    //
    //   const animationName = `win_line_${lineId}`;
    //
    //   // Reset animation to frame 0 for perfect synchronization
    //   spine.state.clearTracks();
    //   spine.state.setAnimation(0, animationName, true);
    //   spine.state.update(0);
    //
    //   spine.alpha = 1;
    //   spine.state.timeScale = 1;
    //   validLineIds.push(lineId);
    // }
    //
    // this.activeLineIds = validLineIds;
    // this.currentLineId = "multiple";
    //
    // // Start manual ticker for active spine updates
    // this._startTicker();
    //
    // // Show container — all requested lines appear at once
    // this.spineContainer.alpha = 1;
  }

  /**
   * Show a single win line animation (for cycling through individual lines)
   * @param {number|string} lineId - The win line ID
   * @param {number[]} [hiddenReels] - reels whose cell on this line is boxed
   *        (no line drawn inside them)
   */
  // showWinLine(lineId) {
  showWinLine(lineId, hiddenReels = null) {
    if (!this.initialized || lineId == null) return;
    const spine = this.winLineSpines[lineId];
    if (!spine || !spine.state) return;
    this._setHidden(lineId, hiddenReels);

    // ── The same line again keeps burning (2026-10-13) ── a single winning
    // line is shown on every cycle step; restarting it each time blinked it
    // and reset its fire. Only a DIFFERENT line switches.
    for (let i = 0; i < this.activeLineIds.length; i++) {
      const prevId = this.activeLineIds[i];
      if (prevId !== lineId) this._parkSpine(this.winLineSpines[prevId]);
    }
    // if (!this.activeLineIds.includes(lineId)) this._startSpine(lineId);
    // this.activeLineIds = [lineId];
    // this.currentLineId = lineId;
    // this._startTicker();
    // this.spineContainer.alpha = 1;
    const isKept = this.activeLineIds.includes(lineId);
    this.activeLineIds = isKept ? [lineId] : [];
    this.currentLineId = lineId;
    this._startDeferred(isKept ? [] : [lineId]);

    // Before (restarted the line on every call):
    // // Clear any pending second-play timeout from previous line
    // if (this._winLineSoundTimeout) {
    //   clearTimeout(this._winLineSoundTimeout);
    //   this._winLineSoundTimeout = null;
    // }
    //
    // if (!this.initialized) {
    //   return;
    // }
    //
    // if (lineId == null) return;
    //
    // const spine = this.winLineSpines[lineId];
    // if (!spine || !spine.state) {
    //   return;
    // }
    //
    // // Hide container during setup
    // this.spineContainer.alpha = 0.001;
    //
    // // Stop ticker while setting up
    // this._stopTicker();
    //
    // // Pause only previously active spines (not all N)
    // for (let i = 0; i < this.activeLineIds.length; i++) {
    //   const prevLineId = this.activeLineIds[i];
    //   const prevSpine = this.winLineSpines[prevLineId];
    //   if (prevSpine && prevSpine.state) {
    //     prevSpine.alpha = 0.001;
    //     prevSpine.state.timeScale = 0;
    //   }
    // }
    //
    // // Reset animation to frame 0
    // const animationName = `win_line_${lineId}`;
    // spine.state.clearTracks();
    // spine.state.setAnimation(0, animationName, true);
    // spine.state.update(0);
    //
    // spine.alpha = 1;
    // spine.state.timeScale = 1;
    //
    // this.activeLineIds = [lineId];
    // this.currentLineId = lineId;
    //
    // // Start manual ticker (1 line = every frame, no skipping)
    // this._startTicker();
    //
    // // Show container
    // this.spineContainer.alpha = 1;
  }

  /**
   * Start `fresh` lines on the NEXT frame — the frame their win boxes start
   * on (WinBoxController._activateSpines) — so a line and its boxes come in
   * together (2026-10-13). Lines already burning keep going meanwhile; a
   * hide before then cancels it.
   */
  _startDeferred(fresh) {
    if (this._deferredRAF) {
      cancelAnimationFrame(this._deferredRAF);
      this._deferredRAF = null;
    }
    if (this.activeLineIds.length > 0) {
      this._startTicker();
      this.spineContainer.alpha = 1;
    }
    if (fresh.length === 0) return;
    this._deferredRAF = requestAnimationFrame(() => {
      this._deferredRAF = null;
      for (let i = 0; i < fresh.length; i++) {
        if (this.activeLineIds.includes(fresh[i])) continue;
        this._startSpine(fresh[i]);
        this.activeLineIds.push(fresh[i]);
      }
      // Their first frame without stacked copies, too.
      this._hideSharedPieces();
      this._startTicker();
      this.spineContainer.alpha = 1;
    });
  }

  /** Start a line's clip from frame 0 and show it. */
  _startSpine(lineId) {
    const spine = this.winLineSpines[lineId];
    if (!spine?.state) return;
    spine.state.clearTracks();
    spine.state.setAnimation(0, `win_line_${lineId}`, true);
    spine.state.update(0);
    // Posed BEFORE it shows, as the boxes are — its first visible frame is
    // the clip's, not the empty setup pose.
    spine.state.apply(spine.skeleton);
    try {
      spine.skeleton.updateWorldTransform();
    } catch (e) {
      // Physics not initialized on some spines — safe to continue
    }
    this._applyHidden(lineId);
    spine.alpha = 1;
    spine.state.timeScale = 1;
  }

  /** Which halves of `lineId` stay blank: both halves touching each reel's cell. */
  _setHidden(lineId, reels) {
    const spine = this.winLineSpines[lineId];
    if (!spine?.skeleton || !Array.isArray(reels) || reels.length === 0) {
      delete this.hiddenByLine[lineId];
      return;
    }
    const sk = spine.skeleton;
    const slots = [];
    const hiddenHalves = new Set();
    for (const r of reels) {
      for (const h of [2 * r + 1, 2 * r + 2]) {
        hiddenHalves.add(h);
        const slot = sk.findSlot(`h${h}`);
        if (slot) slots.push(slot);
      }
      // ...and the round joint on that cell's centre, if the line turns there.
      const joint = sk.findSlot(`j${r}`);
      if (joint) slots.push(joint);
    }
    // ── Soft ends at the box (2026-10-13) ── the half next to a hidden one
    // now ENDS at the box edge: a plain piece is cut there in one straight
    // edge, and the glow band (taller than the ring) showed as hairs past
    // the box. It gets the capped variant on that end instead (the clip may
    // already cap its other end at a corner -> `wb`, both).
    const caps = [];
    for (const h of hiddenHalves) {
      for (const [n, side] of [[h - 1, "R"], [h + 1, "L"]]) {
        if (hiddenHalves.has(n)) continue;
        const slot = sk.findSlot(`h${n}`);
        if (slot) caps.push({ slot, side });
      }
    }
    this.hiddenByLine[lineId] = { slots, caps };
    this._applyHidden(lineId);
  }

  /**
   * After the clip has set its pieces: blank the boxed halves, and cap the
   * halves that end at a box. Swapping the attachment resets the slot's
   * flipbook frame, so the frame is carried over (it keeps burning).
   */
  _applyHidden(lineId) {
    const hidden = this.hiddenByLine[lineId];
    if (!hidden) return;
    const { slots, caps } = hidden;
    for (let i = 0; i < slots.length; i++) slots[i].setAttachment(null);
    for (let i = 0; i < caps.length; i++) {
      const { slot, side } = caps[i];
      const current = slot.getAttachment();
      if (!current) continue;
      // const name = current.name;   // wm | wl | wr | wb
      // const left = name === "wl" || name === "wb" || side === "L";
      // const right = name === "wr" || name === "wb" || side === "R";
      // const want = left && right ? "wb" : left ? "wl" : right ? "wr" : "wm";
      // + wsl / wsr: the short corner ends of 90-degree peaks (2026-10-13).
      const name = current.name;   // wm | wl | wr | wb | wsl | wsr
      const left = name === "wl" || name === "wb" || name === "wsl" || side === "L";
      const right = name === "wr" || name === "wb" || name === "wsr" || side === "R";
      let want = "wm";
      if (left && right) want = "wb";
      else if (left) want = name === "wsl" ? "wsl" : "wl";
      else if (right) want = name === "wsr" ? "wsr" : "wr";
      if (want === name) continue;
      const att = slot.bone.skeleton.getAttachment(slot.data.index, want);
      if (!att) continue;
      const frame = slot.sequenceIndex;
      slot.setAttachment(att);
      slot.sequenceIndex = frame;
    }
  }

  /** Pause a line and hide it (GPU-warm at 0.001). */
  _parkSpine(spine) {
    if (!spine?.state) return;
    spine.alpha = 0.001;
    spine.state.timeScale = 0;
  }

  /**
   * Hide current win line animation(s)
   * Only touches active spines — resets them to clean state
   */
  hideWinLine() {
    // A line still due to start does not.
    if (this._deferredRAF) {
      cancelAnimationFrame(this._deferredRAF);
      this._deferredRAF = null;
    }

    // Stop ticker — no active lines to update
    this._stopTicker();

    // Hide container
    this.spineContainer.alpha = 0.001;

    // Pause and reset only the active spines (not all N)
    for (let i = 0; i < this.activeLineIds.length; i++) {
      const lineId = this.activeLineIds[i];
      const spine = this.winLineSpines[lineId];
      if (spine && spine.state) {
        spine.state.timeScale = 0;
        spine.state.clearTracks();
        // Full skeleton reset — cleans up slot attachments
        // so next show starts from a completely clean slate
        if (spine.skeleton?.setToSetupPose) {
          spine.skeleton.setToSetupPose();
        }
        spine.alpha = 0.001;
      }
    }

    this.activeLineIds = [];
    this.hiddenByLine = {};
    this.rowsByLine = {};
    this.currentLineId = null;
  }

  /**
   * Update spine dimensions and rescale for resize events
   */
  updateDimensionsAndRescale(symbolWidth, symbolHeight, dividerDimensions) {
    if (
      this.symbolWidth === symbolWidth &&
      this.symbolHeight === symbolHeight
    ) {
      return;
    }

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dividerDimensions = dividerDimensions;

    this._scaleAndPositionCache = null;

    if (this.spineContainer && this.initialized) {
      this.scaleAndPositionSpineContainer();
    }
  }

  /**
   * Handle container resize
   */
  resizeToContainer() {
    if (this.spineContainer && this.initialized) {
      this.scaleAndPositionSpineContainer();
    }
  }

  /**
   * Retry initialization if it failed initially
   */
  retryInitialization() {
    if (this.winLineCount === 0 || Object.keys(this.winLineSpines).length === 0) {
      this.initializeSpines();
    }
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.initialized && Object.keys(this.winLineSpines).length > 0;
  }

  /**
   * Clean up all spine instances and resources
   */
  cleanup() {
    if (this._deferredRAF) {
      cancelAnimationFrame(this._deferredRAF);
      this._deferredRAF = null;
    }

    // Stop ticker
    this._stopTicker();

    // Destroy all spine instances
    const lineIds = Object.keys(this.winLineSpines);
    for (let i = 0; i < lineIds.length; i++) {
      const spine = this.winLineSpines[lineIds[i]];
      if (spine) {
        if (spine.state) {
          spine.state.clearTracks();
          spine.state.clearListeners();
        }
        if (spine.parent) {
          spine.parent.removeChild(spine);
        }
        spine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }
    this.winLineSpines = {};

    if (this.spineContainer) {
      if (this.spineContainer.parent) {
        this.spineContainer.parent.removeChild(this.spineContainer);
      }
      this.spineContainer.mask = null;
      this.spineContainer.destroy({ children: true });
      this.spineContainer = null;
    }
    if (this.clipMask && !this.clipMask.destroyed) {
      this.clipMask.parent?.removeChild(this.clipMask);
      this.clipMask.destroy();
    }
    this.clipMask = null;

    this.activeLineIds = [];
    this.hiddenByLine = {};
    this.rowsByLine = {};
    this.currentLineId = null;
    this.winLineCount = 0;
    this.availableAnimations = [];
    this.initialized = false;
  }
}

export default WinLinesController;
